import React from 'react';

/**
 * The bar between the examiner and the question on a wide screen: drag it
 * (or use the arrow keys) to make the examiner bigger or smaller; a double
 * click hands the size back to the layout. The chosen height is remembered
 * in this browser. Hidden on narrow screens, where the sitting stacks and
 * the page scrolls (InterviewSitting.css).
 */
const KEY = 'ivori-interview-stage-height';
export const MIN_STAGE = 180;
// what the question and the answer box keep below the examiner, at least
export const MIN_BELOW = 260;
const STEP = 24;

export function loadStageHeight() {
  try {
    const n = Number(localStorage.getItem(KEY));
    return Number.isFinite(n) && n >= MIN_STAGE ? n : null;
  } catch {
    return null;
  }
}

export function saveStageHeight(height) {
  try {
    if (height == null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(Math.round(height)));
  } catch { /* private mode: the size just isn't remembered */ }
}

export default function StageDivider({ columnRef, height, onResize }) {
  const limits = () => {
    const column = columnRef.current;
    const max = column ? Math.max(MIN_STAGE, column.clientHeight - MIN_BELOW) : MIN_STAGE;
    return (h) => Math.min(max, Math.max(MIN_STAGE, h));
  };
  const current = () => height
    ?? columnRef.current?.querySelector('.is-stage')?.getBoundingClientRect().height
    ?? MIN_STAGE;

  const onPointerDown = (e) => {
    if (e.button !== 0 || !columnRef.current) return;
    e.preventDefault();
    const clamp = limits();
    const top = columnRef.current.getBoundingClientRect().top;
    const move = (ev) => onResize(clamp(ev.clientY - top));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.classList.remove('is-resizing');
    };
    document.body.classList.add('is-resizing');
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    onResize(limits()(current() + (e.key === 'ArrowDown' ? STEP : -STEP)));
  };

  return (
    <div
      className="is-stage-divider"
      role="separator"
      aria-orientation="horizontal"
      aria-label="Resize the examiner. Drag, or use the up and down arrow keys. Double-click to reset."
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => onResize(null)}
    />
  );
}
